import { buildRouteAnalysis } from '../services/route-service.js';
import { state, updateCurrentRoute } from '../lib/state.js';
import { finiteNumberOrNull, hasCoords } from '../lib/app-helpers.js';
import { requestRender } from '../app/events.js';

export function pointsForCurrentView() {
  return state.currentRoute.expandedPoints?.length
    ? state.currentRoute.expandedPoints
    : getRoutePlanPoints(state.currentRoute);
}

export function getRoutePlanPoints(route = state.currentRoute) {
  if (Array.isArray(route?.routePlanPoints) && route.routePlanPoints.length) {
    return route.routePlanPoints.map(point => ({ ...point }));
  }

  const expandedUserPoints = (Array.isArray(route?.expandedPoints) ? route.expandedPoints : [])
    .filter(point => !point?.isBorder);
  if (expandedUserPoints.length) return expandedUserPoints.map(point => ({ ...point }));

  return mergeStopsAndConstraints(actualRouteStops(route), collectRouteConstraints(route))
    .map(point => ({ ...point }));
}

export function normalizeRoutePlanPoints(points = []) {
  // Każde wejście do edycji i każde ponowne przeliczenie czyści duplikaty
  // ręcznych granic. Dzięki temu automatycznie wykryta granica nie jest
  // dokładana ponownie obok istniejącego już punktu wymuszającego.
  const sourcePoints = dedupeManualBorderConstraints(points);
  const plan = sourcePoints.map((point, index) => {
    const routeConstraint = isRouteConstraint(point);
    return {
      ...point,
      id:point.id || crypto.randomUUID(),
      position:index + 1,
      isBorder:false,
      isRouteConstraint:routeConstraint,
      manualRouteConstraint:routeConstraint,
      pointType:routeConstraint ? 'route-via' : (point.pointType === 'border' ? 'stop' : (point.pointType || 'stop')),
      sourceType:routeConstraint ? 'manual-route-via' : (point.sourceType || 'manual'),
    };
  });

  const stopIndexes = plan
    .map((point, index) => !isRouteConstraint(point) ? index : -1)
    .filter(index => index >= 0);
  const stops = stopIndexes.map(index => plan[index]);
  const orderBySegment = new Map();
  const constraintsById = new Map();

  for (let index = 0; index < plan.length; index++) {
    const point = plan[index];
    if (!isRouteConstraint(point)) continue;

    let previousStopIndex = -1;
    let nextStopIndex = -1;
    for (let cursor = index - 1; cursor >= 0; cursor--) {
      if (!isRouteConstraint(plan[cursor])) { previousStopIndex = cursor; break; }
    }
    for (let cursor = index + 1; cursor < plan.length; cursor++) {
      if (!isRouteConstraint(plan[cursor])) { nextStopIndex = cursor; break; }
    }

    if (stops.length >= 2) {
      if (previousStopIndex < 0) previousStopIndex = stopIndexes[0];
      if (nextStopIndex < 0) nextStopIndex = stopIndexes.at(-1);
      if (previousStopIndex === nextStopIndex) {
        const stopPosition = stopIndexes.indexOf(previousStopIndex);
        if (stopPosition < stopIndexes.length - 1) nextStopIndex = stopIndexes[stopPosition + 1];
        else if (stopPosition > 0) previousStopIndex = stopIndexes[stopPosition - 1];
      }
    }

    const after = plan[previousStopIndex] || stops[0] || null;
    const before = plan[nextStopIndex] || stops.at(-1) || null;
    const afterIndex = after ? stops.findIndex(stop => stop.id === after.id) : 0;
    const beforeIndex = before ? stops.findIndex(stop => stop.id === before.id) : Math.min(stops.length - 1, afterIndex + 1);
    const segmentKey = `${after?.id || ''}|${before?.id || ''}`;
    const segmentOrder = (orderBySegment.get(segmentKey) || 0) + 1;
    orderBySegment.set(segmentKey, segmentOrder);

    constraintsById.set(point.id, {
      ...point,
      insertAfterPointId:after?.id || '',
      insertBeforePointId:before?.id || '',
      insertAfterIndex:Math.max(0, afterIndex),
      insertBeforeIndex:Math.max(Math.max(0, afterIndex) + 1, beforeIndex),
      segmentOrder,
    });
  }

  const routePlanPoints = plan.map(point => constraintsById.get(point.id) || point);
  return {
    routePlanPoints,
    originalPoints:routePlanPoints.filter(point => !isRouteConstraint(point)).map(point => ({ ...point })),
    routeConstraints:routePlanPoints.filter(isRouteConstraint).map(point => ({ ...point })),
  };
}

export function commitRoutePlan(points, { render = true } = {}) {
  const normalized = normalizeRoutePlanPoints(points);
  updateCurrentRoute({
    ...normalized,
    dirty:true,
  });
  if (render) requestRender();
  return normalized;
}

export function editablePlanFromCalculatedRoute(route) {
  const expanded = Array.isArray(route?.expandedPoints) ? route.expandedPoints : [];
  if (!expanded.length) return getRoutePlanPoints(route);

  // Bazą są punkty wprowadzone przez użytkownika i wcześniej zapisane punkty
  // wymuszające. Automatycznych granic nie kopiujemy w ciemno, ponieważ po
  // każdym przeliczeniu powstałby drugi wiersz dla tego samego przejścia.
  const savedPlan = dedupeManualBorderConstraints(
    Array.isArray(route?.routePlanPoints) && route.routePlanPoints.length
      ? route.routePlanPoints.map(point => ({ ...point }))
      : getRoutePlanPoints(route)
  );
  const savedById = new Map(savedPlan.filter(point => point?.id).map(point => [point.id, point]));
  const savedManualBorders = savedPlan.filter(isManualBorderConstraint);
  const usedIds = new Set();
  const output = [];

  for (const calculatedPoint of expanded) {
    if (!calculatedPoint?.isBorder) {
      const saved = calculatedPoint?.id ? savedById.get(calculatedPoint.id) : null;
      const point = saved
        ? {
            ...calculatedPoint,
            ...saved,
            // Państwo i współrzędne pochodzą z ostatniego prawidłowego
            // przeliczenia, ale nazwa/adres pozostają dokładnie takie, jak
            // wpisał użytkownik.
            lat:calculatedPoint.lat,
            lon:calculatedPoint.lon,
            countryCode:calculatedPoint.countryCode,
            countryName:calculatedPoint.countryName,
          }
        : { ...calculatedPoint };

      if (point.id && usedIds.has(point.id)) continue;
      output.push(point);
      if (point.id) usedIds.add(point.id);
      continue;
    }

    const matchingManualBorder = findMatchingManualBorder(
      calculatedPoint,
      savedManualBorders,
      usedIds
    );

    // Punkt wymuszający został już zachowany w expandedPoints albo właśnie
    // zastępuje tę automatyczną granicę. Nie tworzymy drugiego wiersza.
    if (matchingManualBorder) {
      if (!usedIds.has(matchingManualBorder.id)) {
        output.push({ ...matchingManualBorder });
        usedIds.add(matchingManualBorder.id);
      }
      continue;
    }

    const pair = borderPairFromPoint(calculatedPoint);
    const converted = {
      ...calculatedPoint,
      id:calculatedPoint.id || crypto.randomUUID(),
      name:normalizeManualBorderName(
        calculatedPoint.name || (pair ? `Granica ${pair}` : 'Granica wymuszona')
      ),
      address:'',
      isBorder:false,
      isRouteConstraint:true,
      manualRouteConstraint:true,
      manualBorderConstraint:true,
      requestedBorderPair:pair,
      pointType:'route-via',
      sourceType:'manual-route-via',
      convertedFromAutoBorder:true,
      sourceBorder:{
        id:calculatedPoint.id || '',
        name:calculatedPoint.name || '',
        countryCode:calculatedPoint.countryCode || '',
        countryName:calculatedPoint.countryName || '',
        lat:calculatedPoint.lat,
        lon:calculatedPoint.lon,
      },
    };
    output.push(converted);
    if (converted.id) usedIds.add(converted.id);
  }

  // Zabezpieczenie dla starszych zapisów: gdy jakiś ręczny punkt nie znalazł
  // się w expandedPoints, zachowujemy go zamiast go bezgłośnie usuwać.
  for (const point of savedPlan) {
    if (!point?.id || usedIds.has(point.id)) continue;
    if (!isRouteConstraint(point)) continue;
    output.push({ ...point });
    usedIds.add(point.id);
  }

  return dedupeManualBorderConstraints(output);
}

export function findMatchingManualBorder(autoBorder, manualBorders = [], usedIds = new Set()) {
  const autoPair = canonicalBorderPair(
    autoBorder?.countryCode || autoBorder?.countryName || autoBorder?.name
  );
  let best = null;
  let bestDistance = Infinity;

  for (const candidate of manualBorders) {
    if (!candidate) continue;

    const sourceId = String(candidate?.sourceBorder?.id || '');
    if (sourceId && autoBorder?.id && sourceId === String(autoBorder.id)) return candidate;

    const candidatePair = canonicalBorderPair(
      candidate.requestedBorderPair
      || candidate.countryCode
      || candidate.countryName
      || candidate.name
      || candidate?.sourceBorder?.countryCode
    );
    if (autoPair && candidatePair && autoPair !== candidatePair) continue;

    const distance = pointDistanceKm(autoBorder, candidate);
    const sourceDistance = pointDistanceKm(autoBorder, candidate?.sourceBorder);
    const nearestDistance = Math.min(distance, sourceDistance);

    // Punkt wymuszający leży zwykle dokładnie na przejściu. Limit 15 km
    // pozwala na różnicę pomiędzy współrzędną użytkownika i punktem granicy
    // wykrytym na geometrii drogi, ale nie łączy różnych przejść.
    if (nearestDistance <= 15 && nearestDistance < bestDistance) {
      best = candidate;
      bestDistance = nearestDistance;
    }
  }

  return best;
}

export function dedupeManualBorderConstraints(points = []) {
  const result = [];

  for (const sourcePoint of Array.isArray(points) ? points : []) {
    const point = { ...sourcePoint };
    if (!isManualBorderConstraint(point)) {
      result.push(point);
      continue;
    }

    const duplicateIndex = result.findIndex(existing => (
      isManualBorderConstraint(existing)
      && manualBordersRepresentSameCrossing(existing, point)
    ));

    if (duplicateIndex < 0) {
      result.push(point);
      continue;
    }

    result[duplicateIndex] = mergeDuplicateManualBorders(result[duplicateIndex], point);
  }

  return result;
}

export function manualBordersRepresentSameCrossing(first, second) {
  if (!first || !second) return false;
  if (first.id && second.id && first.id === second.id) return true;

  const firstSourceId = String(first?.sourceBorder?.id || '');
  const secondSourceId = String(second?.sourceBorder?.id || '');
  if (firstSourceId && secondSourceId && firstSourceId === secondSourceId) return true;

  const firstPair = canonicalBorderPair(
    first.requestedBorderPair || first.countryCode || first.countryName || first.name
  );
  const secondPair = canonicalBorderPair(
    second.requestedBorderPair || second.countryCode || second.countryName || second.name
  );
  if (firstPair && secondPair && firstPair !== secondPair) return false;

  const directDistance = pointDistanceKm(first, second);
  const sourceDistance = Math.min(
    pointDistanceKm(first?.sourceBorder, second),
    pointDistanceKm(first, second?.sourceBorder),
    pointDistanceKm(first?.sourceBorder, second?.sourceBorder)
  );
  return Math.min(directDistance, sourceDistance) <= 1.5;
}

export function mergeDuplicateManualBorders(first, second) {
  const firstScore = manualBorderPriority(first);
  const secondScore = manualBorderPriority(second);
  const preferred = secondScore > firstScore ? second : first;
  const fallback = preferred === first ? second : first;

  return {
    ...fallback,
    ...preferred,
    id:first.id || second.id || crypto.randomUUID(),
    requestedBorderPair:preferred.requestedBorderPair || fallback.requestedBorderPair || '',
    sourceBorder:preferred.sourceBorder || fallback.sourceBorder || null,
  };
}

export function manualBorderPriority(point) {
  let score = 0;
  if (point?.editedAt) score += 100;
  if (point?.createdAt && !point?.convertedFromAutoBorder) score += 30;
  if (point?.requestedBorderPair) score += 20;
  if (!isPlaceholderPointName(point?.name)) score += 10;
  if (hasCoords(point)) score += 5;
  return score;
}

export function isPlaceholderPointName(value) {
  return /^(?:nowy punkt|punkt bez nazwy|granica xx\s*\/\s*yy|granica wymuszona)$/i
    .test(String(value || '').trim());
}

export function pointDistanceKm(first, second) {
  const lat1 = finiteNumberOrNull(first?.lat);
  const lon1 = finiteNumberOrNull(first?.lon);
  const lat2 = finiteNumberOrNull(second?.lat);
  const lon2 = finiteNumberOrNull(second?.lon);
  if ([lat1, lon1, lat2, lon2].some(value => value === null)) return Infinity;

  const toRad = value => value * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}


export function normalizeRouteConstraintName(value) {
  const text = String(value || '')
    .replace(/^Granica\s+[A-Z]{2}\s*\/\s*[A-Z]{2}\s*[–-]?\s*/i, '')
    .replace(/^Granica\s+/i, '')
    .trim();
  return text || 'Ręcznie wskazany punkt przebiegu';
}

export function parseBorderPairFromText(value) {
  const text = String(value || '').toUpperCase().replace(/\s+/g, ' ');
  const direct = text.match(/(?:^|[^A-Z])([A-Z]{2})\s*[\/–—-]\s*([A-Z]{2})(?:[^A-Z]|$)/);
  if (direct) return `${direct[1]}/${direct[2]}`;

  const aliases = {
    POLSKA:'PL', POLAND:'PL',
    CZECHY:'CZ', CZECHIA:'CZ',
    SLOWACJA:'SK', SŁOWACJA:'SK', SLOVAKIA:'SK',
    AUSTRIA:'AT',
    WEGRY:'HU', WĘGRY:'HU', HUNGARY:'HU',
    SLOWENIA:'SI', SŁOWENIA:'SI', SLOVENIA:'SI',
    CHORWACJA:'HR', CROATIA:'HR',
    NIEMCY:'DE', GERMANY:'DE',
    RUMUNIA:'RO', ROMANIA:'RO',
  };
  const normalized = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const codes = [];
  for (const [name, code] of Object.entries(aliases)) {
    const normalizedName = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (normalized.includes(normalizedName) && !codes.includes(code)) codes.push(code);
  }
  if (codes.length >= 2) return `${codes[0]}/${codes[1]}`;
  return '';
}

export function borderPairFromPoint(point) {
  return parseBorderPairFromText(point?.requestedBorderPair)
    || parseBorderPairFromText(point?.countryCode)
    || parseBorderPairFromText(point?.name)
    || parseBorderPairFromText(point?.countryName);
}

export function normalizeManualBorderName(value, pair = '') {
  const requestedPair = pair || parseBorderPairFromText(value);
  const text = String(value || '').trim();
  if (requestedPair) {
    const suffix = text
      .replace(/^Granica\s+/i, '')
      .replace(new RegExp(requestedPair.replace('/', '\\s*[\\/–—-]\\s*'), 'i'), '')
      .replace(/^[\s–—-]+|[\s–—-]+$/g, '')
      .trim();
    return `Granica ${requestedPair}${suffix ? ` – ${suffix}` : ''}`;
  }
  return text || 'Granica wymuszona';
}

export function isManualBorderConstraint(point) {
  return Boolean(
    point?.manualBorderConstraint
    || point?.convertedFromAutoBorder
    || point?.requestedBorderPair
    || (isRouteConstraint(point) && /^Granica\b/i.test(String(point?.name || '')))
  );
}

export function canonicalBorderPair(value) {
  const pair = parseBorderPairFromText(value);
  if (!pair) return '';
  return pair.split('/').sort().join('/');
}

export function missingRequestedBorderPairs(routePlanPoints = [], expandedPoints = []) {
  const required = [...new Set(
    routePlanPoints
      .filter(isManualBorderConstraint)
      .map(point => canonicalBorderPair(point.requestedBorderPair || point.name))
      .filter(Boolean)
  )];
  const actual = new Set(
    expandedPoints
      .filter(point => point?.isBorder)
      .map(point => canonicalBorderPair(point.countryCode || point.name || point.countryName))
      .filter(Boolean)
  );
  return required.filter(pair => !actual.has(pair));
}

export function isRouteConstraint(point) {
  return Boolean(
    point?.isRouteConstraint
    || point?.manualRouteConstraint
    || point?.pointType === 'route-via'
    || point?.sourceType === 'manual-route-via'
  );
}

export function actualRouteStops(route) {
  const source = Array.isArray(route?.originalPoints) ? route.originalPoints : [];
  return source.filter(point => !point?.isBorder && !isRouteConstraint(point));
}

export function collectRouteConstraints(route) {
  const candidates = [
    ...(Array.isArray(route?.routeConstraints) ? route.routeConstraints : []),
    ...(Array.isArray(route?.routePlanPoints) ? route.routePlanPoints : []),
    ...(Array.isArray(route?.expandedPoints) ? route.expandedPoints : []),
    ...(Array.isArray(route?.originalPoints) ? route.originalPoints : []),
  ].filter(isRouteConstraint);

  const unique = new Map();
  for (const point of candidates) {
    const key = point.id || `${point.insertAfterPointId || ''}|${point.insertBeforePointId || ''}|${point.lat}|${point.lon}`;
    if (!unique.has(key)) unique.set(key, { ...point });
  }
  return [...unique.values()];
}

export function constraintAnchors(constraint, stops) {
  const afterIndexById = stops.findIndex(point => point.id === constraint.insertAfterPointId);
  const beforeIndexById = stops.findIndex(point => point.id === constraint.insertBeforePointId);
  const afterIndex = afterIndexById >= 0
    ? afterIndexById
    : Math.max(0, Math.min(stops.length - 2, Number(constraint.insertAfterIndex) || 0));
  const beforeIndex = beforeIndexById >= 0
    ? beforeIndexById
    : Math.min(stops.length - 1, Math.max(afterIndex + 1, Number(constraint.insertBeforeIndex) || afterIndex + 1));
  return {
    afterPointId:stops[afterIndex]?.id || '',
    beforePointId:stops[beforeIndex]?.id || '',
    afterIndex,
    beforeIndex,
  };
}

export function findRouteSegmentAnchors(selected, expandedPoints, stops) {
  const points = Array.isArray(expandedPoints) ? expandedPoints : [];
  const stopIds = new Set(stops.map(point => point.id));
  let selectedIndex = points.findIndex(point => point.id && selected?.id && point.id === selected.id);
  if (selectedIndex < 0) {
    selectedIndex = points.findIndex(point => (
      Boolean(point?.isBorder) === Boolean(selected?.isBorder)
      && Math.abs(Number(point?.lat) - Number(selected?.lat)) < 0.00001
      && Math.abs(Number(point?.lon) - Number(selected?.lon)) < 0.00001
    ));
  }

  let after = null;
  let before = null;
  for (let index = selectedIndex - 1; index >= 0; index--) {
    const point = points[index];
    if (!point?.isBorder && !isRouteConstraint(point) && stopIds.has(point.id)) {
      after = point;
      break;
    }
  }
  for (let index = selectedIndex + 1; index < points.length; index++) {
    const point = points[index];
    if (!point?.isBorder && !isRouteConstraint(point) && stopIds.has(point.id)) {
      before = point;
      break;
    }
  }

  const afterIndex = after ? stops.findIndex(point => point.id === after.id) : 0;
  const beforeIndex = before ? stops.findIndex(point => point.id === before.id) : Math.min(stops.length - 1, afterIndex + 1);
  const safeAfterIndex = Math.max(0, Math.min(stops.length - 2, afterIndex));
  const safeBeforeIndex = Math.max(safeAfterIndex + 1, Math.min(stops.length - 1, beforeIndex));

  return {
    afterPointId:stops[safeAfterIndex]?.id || '',
    beforePointId:stops[safeBeforeIndex]?.id || '',
    afterIndex:safeAfterIndex,
    beforeIndex:safeBeforeIndex,
  };
}

export function nextConstraintOrder(constraints, afterPointId, beforePointId, selected, expandedPoints) {
  const sameSegment = constraints
    .filter(item => item.insertAfterPointId === afterPointId && item.insertBeforePointId === beforePointId)
    .sort((a,b) => Number(a.segmentOrder || 0) - Number(b.segmentOrder || 0));
  if (!sameSegment.length) return 1;

  const selectedIndex = expandedPoints.findIndex(point => point.id && selected?.id && point.id === selected.id);
  if (selectedIndex >= 0) {
    const previousConstraints = expandedPoints
      .slice(0, selectedIndex)
      .filter(point => isRouteConstraint(point)
        && point.insertAfterPointId === afterPointId
        && point.insertBeforePointId === beforePointId);
    return previousConstraints.length + 1;
  }
  return Math.max(...sameSegment.map(item => Number(item.segmentOrder) || 0)) + 1;
}

export function mergeStopsAndConstraints(stops, constraints) {
  const normalized = constraints.map(constraint => {
    const anchors = constraintAnchors(constraint, stops);
    return { ...constraint, ...{
      insertAfterPointId:anchors.afterPointId,
      insertBeforePointId:anchors.beforePointId,
      insertAfterIndex:anchors.afterIndex,
      insertBeforeIndex:anchors.beforeIndex,
    }};
  });

  const byAfter = new Map();
  for (const constraint of normalized) {
    const key = constraint.insertAfterPointId;
    if (!byAfter.has(key)) byAfter.set(key, []);
    byAfter.get(key).push(constraint);
  }
  for (const list of byAfter.values()) {
    list.sort((a,b) => Number(a.segmentOrder || 0) - Number(b.segmentOrder || 0));
  }

  const planningPoints = [];
  for (const stop of stops) {
    planningPoints.push({ ...stop });
    for (const constraint of byAfter.get(stop.id) || []) {
      planningPoints.push({
        ...constraint,
        id:constraint.id || crypto.randomUUID(),
        isBorder:false,
        isRouteConstraint:true,
        manualRouteConstraint:true,
        pointType:'route-via',
        sourceType:'manual-route-via',
      });
    }
  }
  return planningPoints;
}

export async function rebuildRouteFromPlan(planningPoints, onProgress = () => {}) {
  const normalized = normalizeRoutePlanPoints(planningPoints);
  if (normalized.routePlanPoints.length < 2 || normalized.originalPoints.length < 2) {
    throw new Error('Potrzebne są co najmniej dwa zwykłe punkty/przystanki trasy.');
  }

  onProgress('Wyznaczam cały przebieg drogowy przez wskazane punkty…');
  const analysis = await buildRouteAnalysis(normalized.routePlanPoints, onProgress);
  const metadataById = new Map(normalized.routePlanPoints.map(point => [point.id, point]));

  const mergeUserMetadata = point => {
    const metadata = point?.id ? metadataById.get(point.id) : null;
    if (!metadata) return { ...point };
    return {
      ...point,
      ...metadata,
      // Nazwa i adres wpisane przez użytkownika mają pierwszeństwo. Dane
      // przestrzenne i wykryte państwo pochodzą z bieżącego przeliczenia.
      name:String(metadata.name || point.name || '').trim() || point.name,
      address:metadata.address ?? point.address ?? '',
      lat:point.lat,
      lon:point.lon,
      countryCode:point.countryCode,
      countryName:point.countryName,
      isBorder:Boolean(point.isBorder),
    };
  };

  const enrichedPlan = analysis.originalPoints.map(mergeUserMetadata);
  const finalized = normalizeRoutePlanPoints(enrichedPlan);
  const finalizedById = new Map(finalized.routePlanPoints.map(point => [point.id, point]));

  const mergeFinalMetadata = (point, position = point?.position) => {
    const metadata = point?.id ? finalizedById.get(point.id) : null;
    if (!metadata) return { ...point, position };
    return {
      ...point,
      ...metadata,
      name:String(metadata.name || point.name || '').trim() || point.name,
      address:metadata.address ?? point.address ?? '',
      lat:point.lat,
      lon:point.lon,
      countryCode:point.countryCode,
      countryName:point.countryName,
      isBorder:Boolean(point.isBorder),
      position,
    };
  };

  const expandedPoints = analysis.expandedPoints.map((point, index) => mergeFinalMetadata(point, index + 1));
  const expandedById = new Map(expandedPoints.filter(point => point?.id).map(point => [point.id, point]));
  const sections = analysis.sections.map(section => ({
    ...section,
    points:(section.points || []).map(point => expandedById.get(point.id) || mergeFinalMetadata(point, point.position)),
  }));

  return {
    ...analysis,
    originalPoints:finalized.originalPoints,
    routeConstraints:finalized.routeConstraints,
    routePlanPoints:finalized.routePlanPoints,
    expandedPoints,
    sections,
  };
}

export async function rebuildRouteWithConstraints(stops, constraints, onProgress = () => {}) {
  return rebuildRouteFromPlan(mergeStopsAndConstraints(stops, constraints), onProgress);
}

export function routeCountriesLabel(sections = []) {
  const countries = [];
  for (const section of sections) {
    const name = section?.countryName || section?.countryCode || '';
    if (name && countries.at(-1) !== name) countries.push(name);
  }
  return countries.join(' → ');
}
